'use client';

import { useTranslations } from 'next-intl';

import { alpha } from '@mui/material/styles';
import { Box, Stack, Container, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';

export default function HomeWhatCan() {
  const t = useTranslations('Home.whatCan');

  const CARDS = [
    {
      icon: 'material-symbols:search-insights',
      title: t('insights.title'),
      description: t('insights.description'),
    },
    {
      icon: 'solar:users-group-rounded-bold-duotone',
      title: t('excel.title'),
      description: t('excel.description'),
    },
    {
      icon: 'solar:graph-up-bold-duotone',
      title: t('analytics.title'),
      description: t('analytics.description'),
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 14 },
        bgcolor: 'background.default',
      }}
    >
      <Container>
        <Stack
          alignItems="center"
          spacing={1.5}
          sx={{ maxWidth: 560, mx: 'auto', mb: { xs: 6, md: 9 }, textAlign: 'center', px: 1 }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: 'primary.main',
            }}
          >
            {t('overline')}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Barlow, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(28px, 3.5vw, 42px)',
              lineHeight: 1.1,
              letterSpacing: '-1px',
              color: 'text.primary',
            }}
          >
            {t('title')}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {CARDS.map((card, index) => {
            const isDark = index === 1;
            return (
              <Box
                key={card.title}
                sx={(theme) => ({
                  p: { xs: 3.5, md: 4.5 },
                  borderRadius: 2.5,
                  bgcolor: isDark
                    ? theme.palette.mode === 'dark'
                      ? 'grey.900'
                      : 'grey.800'
                    : theme.palette.mode === 'dark'
                      ? 'background.paper'
                      : 'grey.100',
                  border: '1px solid',
                  borderColor: isDark
                    ? theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.04)
                      : 'grey.700'
                    : theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.08)
                      : 'grey.200',
                  transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 20px 48px ${alpha(theme.palette.grey[900], theme.palette.mode === 'dark' ? 0.4 : 0.1)}`,
                  },
                })}
              >
                <Box
                  sx={(theme) => ({
                    width: 52,
                    height: 52,
                    borderRadius: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3.5,
                    bgcolor: isDark
                      ? alpha(theme.palette.common.white, 0.08)
                      : theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.04)
                        : '#ffffff',
                    boxShadow: isDark
                      ? 'none'
                      : theme.palette.mode === 'dark'
                        ? 'none'
                        : `0 2px 12px ${alpha(theme.palette.grey[900], 0.08)}`,
                  })}
                >
                  <Iconify
                    icon={card.icon}
                    width={26}
                    sx={{ color: isDark ? 'primary.light' : 'primary.main' }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 700,
                    fontSize: 20,
                    color: isDark ? 'common.white' : 'text.primary',
                    mb: 1.5,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    lineHeight: 1.65,
                    color: isDark ? alpha('#ffffff', 0.55) : 'text.secondary',
                  }}
                >
                  {card.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
